/**
 * useBorderRadius Hook
 * Calculates border radius with morphing support
 */

import { useMemo } from 'react';
import { SIZE_CONFIG } from '../constants';
import type { ButtonShape, ButtonSize } from '../types';

// ============================================================================
// TYPES
// ============================================================================

export interface UseBorderRadiusProps {
  customBorderRadius?: number | string;
  isPressed: boolean;
  isSelected: boolean;
  morphShape: boolean;
  shape: ButtonShape;
  size: ButtonSize;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for calculating border radius with morphing support
 */
export function useBorderRadius({
  customBorderRadius,
  isPressed,
  isSelected,
  morphShape,
  shape,
  size,
}: UseBorderRadiusProps): number | string {
  return useMemo(() => {
    if (customBorderRadius !== undefined) {
      return customBorderRadius;
    }

    const config = SIZE_CONFIG[size];
    let radius = shape === 'square' ? config.squareBorderRadius : config.borderRadius;

    // Apply shape morphing
    if (morphShape && (isPressed || isSelected)) {
      radius = Math.max(radius * 0.5, config.squareBorderRadius);
    }

    return radius;
  }, [customBorderRadius, isPressed, isSelected, morphShape, shape, size]);
}

export default useBorderRadius;
